import { TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import { HudIconProps } from './Icons/HudIcons';

export function formatDate(date: Date): string {
  return date
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'numeric',
    })
    .replace(/ /g, '/');
}

export function formatDateShort(date: Date): string {
  return date
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'numeric',
    })
    .replace(/ /g, '/');
}

export function formatTime12Hour(date: Date): string {
  // 12:00 AM format
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date);
}

export function formatTime12HourWithDay(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
    .format(date)
    .replace(', ', '•');
}

export function formatDateTime(date: Date, relative = false): string {
  if (relative) {
    return formatRelativeTime(date);
  }
  return date
    .toLocaleString('en-GB', {
      day: 'numeric',
      month: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      year: '2-digit',
    })
    .replace(/ /g, '/')
    .replace(/,\//g, ', ');
}

export function formatRelativeDateTime(date: Date): string {
  const relative = formatDateTime(date, true);
  return relative.replace('-', '') + ' ago';
}

export function formatNumber(num: number | null | undefined, decimalPlaces = 2): string {
  if (num === null || num === undefined) {
    return '';
  } else if (num === 0) {
    return `0`;
  }
  const minimalNumber = Math.pow(10, -decimalPlaces);
  if (num < minimalNumber) {
    // For example, if the number is less than 0.001, return "< 0.001"
    return `${minimalNumber.toString()}(<<)`;
  }
  if (Math.abs(num) < 10) return parseFloat(num.toFixed(2)).toString();
  if (Math.abs(num) < 100) return parseFloat(num.toFixed(1)).toString();

  return parseFloat(num.toFixed(0)).toLocaleString();
}

export function formatBigNumbers(
  num: number | null | undefined,
  decimalPlaces = 2,
): string {
  if (num === null || num === undefined) {
    return '';
  } else if (num === 0) {
    return `0`;
  }
  const minimalNumber = Math.pow(10, -decimalPlaces);
  if (num < minimalNumber) {
    // For example, if the number is less than 0.001, return "< 0.001"
    return `${minimalNumber.toString()}(<<)`;
  }
  if (Math.abs(num) < 10) return parseFloat(num.toFixed(2)).toString();
  if (Math.abs(num) < 100) return parseFloat(num.toFixed(1)).toString();

  // If no. > 1000000000 return B
  if (Math.abs(num) >= 1_000_000_000) {
    const b = num / 1_000_000_000;
    if (Math.abs(b) < 10) return parseFloat(b.toFixed(2)).toString() + 'B';
    if (Math.abs(b) < 100) return parseFloat(b.toFixed(1)).toString() + 'B';
    return parseFloat(b.toFixed(0)).toString() + 'B';
  }

  // If no . > 1000000 return M
  if (Math.abs(num) >= 1_000_000) {
    const m = num / 1_000_000;
    if (Math.abs(m) < 10) return parseFloat(m.toFixed(2)).toString() + 'M';
    if (Math.abs(m) < 100) return parseFloat(m.toFixed(1)).toString() + 'M';
    return parseFloat(m.toFixed(0)).toString() + 'M';
  }

  // If no. > 1000 return Nk
  if (Math.abs(num) >= 1000) {
    const k = num / 1000;
    if (Math.abs(k) < 10) return parseFloat(k.toFixed(2)).toString() + 'k';
    if (Math.abs(k) < 100) return parseFloat(k.toFixed(1)).toString() + 'k';
    return parseFloat(k.toFixed(0)).toString() + 'k';
  }

  return parseFloat(num.toFixed(0)).toLocaleString();
}

export function formatPrice(num: undefined | number | null, decimalPlaces = 2): string {
  if (num === null || num === undefined) {
    return '';
  }
  return `$${formatNumber(num, decimalPlaces)}`;
}

type CostOverviewPeriod = 'hourly' | 'daily' | 'monthly' | 'yearly';
export function convertCostTimePeriod(
  cost: { value: number; period: CostOverviewPeriod } | undefined,
  to_period: CostOverviewPeriod,
): number {
  let multiplier = 1;
  const hoursInTimePeriod = {
    hourly: 1,
    daily: 24,
    monthly: 24 * 30,
    yearly: 24 * 365,
  };

  if (cost?.period === 'yearly') {
    multiplier = hoursInTimePeriod[to_period] / 24 / 365;
  } else if (cost?.period === 'monthly') {
    multiplier = hoursInTimePeriod[to_period] / 24 / 30;
  } else if (cost?.period === 'daily') {
    multiplier = hoursInTimePeriod[to_period] / 24;
  } else if (cost?.period === 'hourly') {
    multiplier = hoursInTimePeriod[to_period];
  }

  return multiplier * (cost?.value || 0);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getByPath(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;

  for (let i = 0; i < parts.length; i++) {
    if (current[parts[i]] == null) {
      return undefined;
    } else {
      current = current[parts[i]];
    }
  }

  return current;
}

enum TimeUnit {
  MILLISECONDS = 'milliseconds',
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
}

export function formatDuration(
  num: number | null | undefined,
  decimalPlaces = 2,
  unit = TimeUnit.MILLISECONDS,
): string {
  if (num === null || num === undefined) {
    return '';
  } else if (num === 0) {
    return `0`;
  }

  // convert to milliseconds
  if (unit === TimeUnit.SECONDS) {
    num = num * 1000;
  } else if (unit === TimeUnit.MINUTES) {
    num = num * 1000 * 60;
  } else if (unit === TimeUnit.HOURS) {
    num = num * 1000 * 60 * 60;
  }

  const minNano = 0.00000001;
  // if less then 0.01 nanoseconds return 0.01ns(<<)
  if (num < minNano) {
    return `0.01ns<<`;
  }
  // if between 0.01 and 1000  nanoseconds, return nanoseconds with 2 decimal places (if there are decimal places)
  else if (num < 0.001) {
    return `${(num * 1_000_000).toFixed(decimalPlaces).toString()}ns`;
  }
  //  if between 1 microsecond and 999 microseconds, return microseconds with 2 decimal places (if there are decimal places)
  else if (num < 1) {
    return `${(num * 1000).toFixed(decimalPlaces)}µs`;
  } else if (num < 1000) {
    return `${num.toFixed(decimalPlaces).toString()}ms`;
  }
  // if between 1 second and 59 seconds, return seconds with 2 decimal places (if there are decimal places)
  else if (num < 60000) {
    return `${(num / 1000).toFixed(2)}s`;
  }
  // if between 1 minute and 59 minutes, return mm:ss
  else if (num < 3600000) {
    const minutes = Math.floor(num / 60000);
    const seconds = Math.floor((num - minutes * 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}m`;
  }
  // return hh:mm
  else {
    const hours = Math.floor(num / 3600000);
    const minutes = Math.floor((num - hours * 3600000) / 60000);
    return `${hours}:${minutes.toString().padStart(2, '0')}h`;
  }
}

export function formatPercentage(
  rate: number | null | undefined,
  decimalPlaces = 1,
): string {
  if (rate === null || rate === undefined || rate === 0) {
    return '0%';
  } else if (rate === 1) {
    return '100%';
  }

  const percent = 100 * rate;

  // Handle special case for values less than 0.01%
  if (Math.abs(percent) < 0.01) {
    return '0.01%<<';
  }

  if (percent > 100 || percent < -100) {
    return `${percent.toLocaleString('en-GB', {
      maximumFractionDigits: 0,
    })}%`;
  }

  const fixed = percent.toFixed(decimalPlaces);

  if (decimalPlaces === 0) {
    return `${fixed}%`;
  }
  const rounded =
    fixed === `0.${'0'.repeat(decimalPlaces)}` ? 0.01 : parseFloat(fixed);
  // Convert number to string with decimalPlaces decimal places
  const formatted = rounded % 1 === 0 ? Math.floor(rounded) : rounded;
  // Remove trailing zeros and return with percentage symbol
  return `${formatted}%`;
}

export function formatRelativeTime(date: Date, now = new Date()): string {
  const diff = now.getTime() - date.getTime();
  const diffInSeconds = diff / 1000;
  const diffInMinutes = diffInSeconds / 60;
  const diffInHours = diffInMinutes / 60;
  const diffInDays = diffInHours / 24;
  const diffInMonths = diffInDays / 30;
  const diffInYears = diffInDays / 365;

  if (Math.round(diffInSeconds) < 60) return `-${Math.round(diffInSeconds)}s`;
  if (Math.round(diffInMinutes) < 60) return `-${Math.round(diffInMinutes)}m`;
  if (Math.round(diffInHours) < 48) {
    let minutes = Math.round(diffInMinutes % 60);
    let hours = Math.floor(diffInHours);
    if (minutes === 60) {
      hours = hours + 1;
      minutes = 0;
    }
    return minutes === 0 ? `-${hours}h` : `-${hours}h ${minutes}m`;
  }
  if (Math.round(diffInDays) < 30) {
    let hours = Math.round(diffInHours % 24);
    let days = Math.floor(diffInDays);
    if (hours === 24) {
      days = days + 1;
      hours = 0;
    }
    return hours === 0 ? `-${days}d` : `-${days}d ${hours}h`;
  }
  if (Math.round(diffInMonths) < 12) {
    let days = Math.round(diffInDays % 30);
    let months = Math.floor(diffInMonths);
    if (days === 30) {
      months = months + 1;
      days = 0;
    }
    return days === 0 ? `-${months}M` : `-${months}M ${days}d`;
  }
  let months = Math.round(diffInMonths % 12);
  let years = Math.floor(diffInYears);
  if (months === 12) {
    years = years + 1;
    months = 0;
  }
  return months === 0 ? `-${years}y` : `-${years}y ${months}M`;
}

export function getDisplayMethod(methods: string[]): string {
  if (methods.length === 0) {
    return '';
  }
  if (methods.length === 1) {
    return methods[0].toLocaleUpperCase();
  }
  const methodOrder = [
    'GET',
    'POST',
    'PUT',
    'DELETE',
    'PATCH',
    'OPTIONS',
    'CONNECT',
    'TRACE',
    'HEAD',
  ];
  const upperCaseMethods = methods.map((method) => method.toUpperCase());
  // find the first method in the from methodOrder that is in the methods array or return the first method in the methods array
  const displayMethod =
    methodOrder.find((method) => upperCaseMethods.includes(method)) ||
    upperCaseMethods[0];
  return `${displayMethod}(+${methods.length - 1})`;
}

export function getEndOfDay(date: Date): Date {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59);
  return endOfDay;
}

export type FlowType = 'ENDPOINT' | 'KAFKA' | 'SQS';

export function getIconForFlow(
  flowType: FlowType | undefined,
): HudIconProps['icon'] {
  return (flowType && ['KAFKA', 'SQS'].includes(flowType))
    ? 'entities.messageQueue'
    : 'webviewMenu.endpoint'
}

// from https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
export function isMobile() {
  let check = false;
  // eslint-disable-next-line no-useless-escape
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
  return check;
}

export function isTimeFrameLessInRange(rangeInMs: number, timeFrame: TimeFrame, firstSeenAt?: Date): boolean {
  if (typeof timeFrame === 'string') {
    const timeFrameInMs = {
      hour: 1000 * 60 * 60,
      day: 1000 * 60 * 60 * 24,
      week: 1000 * 60 * 60 * 24 * 7,
      '14days': 1000 * 60 * 60 * 24 * 14,
      '30days': 1000 * 60 * 60 * 24 * 30,
      '2months': 1000 * 60 * 60 * 24 * 30 * 2,
    }[timeFrame];
    if (!timeFrameInMs) {
      return false;
    }
    return timeFrameInMs < rangeInMs;
  } else if (timeFrame && timeFrame.start && timeFrame.end) {
    const timeFrameStart = firstSeenAt && firstSeenAt > timeFrame.start ? firstSeenAt : timeFrame.start;
    return (timeFrame.end.getTime() - timeFrameStart.getTime()) < rangeInMs;
  }
  return false;
}

export function isTimeFrameLessThanDay(timeFrame: TimeFrame, firstSeenAt?: Date): boolean {
  return isTimeFrameLessInRange(1000 * 60 * 60 * 24, timeFrame, firstSeenAt);
}

export function isTimeFrameLessThan2Days(timeFrame: TimeFrame, firstSeenAt?: Date): boolean {
  return isTimeFrameLessInRange(2 * 1000 * 60 * 60 * 24, timeFrame, firstSeenAt);
}

export function isDefined<T>(
  value: T | null | undefined,
): value is Exclude<T, null | undefined> {
  return value !== undefined && value !== null;
}
